/**
 * Home Feature - Simplified Chinese (zh-CN)
 */

import type { HomeLocale } from './types';

const zhCN: HomeLocale = {
    home: {
        title: '用 AI 加速您的专利撰写',
        subtitle: '从交底书到审查意见答复，智能完成专利全流程文书',

        tabs: {
            patent: '专利申请',
            disclosure: '交底书',
            oar: '审查意见答复',
            review: '核稿审查',
        },

        cardTitles: {
            patent: '专利申请',
            disclosure: '交底书',
            oar: '审查意见答复',
            review: '核稿审查',
        },

        placeholders: {
            patent: '请描述您的技术方案、解决的问题以及创新之处。\n\n您也可以输入 {\'@\'} 使用模板或上传文件。',
            disclosure: '请描述您的发明构思、解决的技术问题以及关键创新点。\n\n您也可以输入 {\'@\'} 使用模板或上传文件。',
            oar: '请在此粘贴审查意见通知书内容，或描述您需要答复的驳回理由。',
            review: '可输入待核稿文字或上传待核稿文件',
        },

        review: {
            title: '核稿审查',
            subTabs: {
                patent_application: '专利申请文件',
            },
            placeholder: '可输入待核稿文字或上传待核稿文件',
            patentOffice: '受理局',
            upload: {
                addMaterials: '添加材料',
                file: '上传待核稿文件',
                figure: '上传附图',
                disclosure: '上传交底书',
                customRule: '上传自定义规则',
                customRuleChip: '{count} 条自定义规则',
                ruleLibrary: '规则库选择',
                applicationType: '申请类型',
                applicationTypeValue: '发明',
                pendingReviewStatus: '待核稿文件',
                dropZone: {
                    releaseToUpload: '松开以上传文件',
                    unsupportedFileType: '不支持该文件格式，请上传 DOC、DOCX、PDF 或 TXT 文件',
                },
            },
            messages: {
                pleaseUploadOrInput: '请输入内容或上传待核稿文件',
                generating: '正在核稿审查中...',
                submitFailed: '提交失败，请重试',
            },
            prosecutionTip: '上传待核稿文件，选择规则库，即可开始核稿',
        },

        placeholder: {
            default: '描述您的发明或输入 {\'@\'} 使用模板',
            template: '输入 {\'@\'} 浏览专利模板',
            example1: '例如：一种用于图像识别的机器学习系统...',
            example2: '例如：一种高容量改进型电池设计...',
            example3: '例如：{\'@\'} USPTO 软件专利模板',
        },

        workMode: {
            agent: '智能体',
            agentDescription: '自动完成',
            copilot: '助手',
            copilotDescription: '逐步审阅',
        },

        actions: {
            addFiles: '添加文件',
            addFilesTooltip: '上传文件（PDF、Word、TXT）',
            readOnlyTip: '您只有只读权限，无法上传文件',
            dropToAdd: '松开以添加文件',
            style: '风格',
            styleTooltip: '选择撰写风格',
            template: '模板',
            templateTooltip: '选择专利模板',
            voiceInput: '语音输入',
            voiceInputTooltip: '语音输入',
            voiceInputSoon: '语音输入功能即将推出...',
            generate: '开始',
            loggingIn: '登录中...',
            startDrafting: '开始撰写',
            draftingStyle: '撰写风格',
            selectTemplate: '选择模板',
            removeFile: '移除',
            loginNow: '立即登录',
            changeTemplate: '点击更改模板',
            featureDisabledTip: '您没有该功能的使用权限，请联系管理员',
            personalQuotaExhaustedTip: '您的个人额度已用完，请联系管理员分配额度',
        },

        upload: {
            dragOrClick: '拖拽文件到此处，或点击上传',
            supportedFormats: '支持 PDF、Word、TXT 及图片文件',
        },

        quickStart: {
            title: '快速开始常用撰写任务：',
            disclosure: '撰写交底书',
            specification: '撰写说明书',
            claims: '撰写权利要求',
            oar: '撰写答复',
            figures: '设计附图',
        },

        messages: {
            filesAdded: '已添加 {count} 个文件',
            pleaseEnterContent: '请输入内容或上传文件',
            pleaseUploadFile: '请上传审查意见文件',
            generating: '正在生成专利文档...',
            navigationFailed: '导航失败',
            styleApplied: '已应用风格：{name}',
            templateApplied: '已选择模板：{name}',
            mentionApplied: '已选择{type}：{name}',
            templateLoading: '正在加载模板：{name}',
            submitFailed: '提交失败，请重试',
            invalidFile: '无效的文件',
            uploadFailed: '文件上传失败，请重试',
            filesUploading: '文件正在上传中，请稍候',
            fileStatusUploading: '上传中...',
            fileStatusFailed: '上传失败',
            someFilesNotUploaded: '部分文件上传失败，请重新上传',
            emptyInput: '请输入内容或上传文件',
            emptyOarInput: '请输入专利申请号或上传文件',
            fileProcessed: '文件 {name} 解析完成',
            fileProcessFailed: '文件 {name} 解析失败',
            fileProcessingTimeout: '文件解析超时,请重新上传',
            filesProcessing: '文件正在解析中,请稍候...',
            filesNotReady: '部分文件尚未解析完成,请等待后再创建任务',
            fileAlreadyExists: '文件 {name} 已存在，直接使用',
            defaultPromptForPatent: '请解析上传的文档，生成技术交底书分析报告和专利申请文档。',
            defaultPromptForDisclosure: '按照技术交底书分析的标准流程处理文件，生成技术交底书',
            loginRequired: '请先登录后再开始撰写，您的工作将保存到账户中。',
        },

        // 统一的第一句话配置（根据任务类型和场景自动选择）
        initialPrompts: {
            patent_draft: {
                withFiles: '请解析上传的文档，生成技术交底书分析报告和专利申请文档。',
                empty: '请开始撰写专利申请',
            },
            disclosure: {
                withFiles: '按照技术交底书分析的标准流程处理文件，生成技术交底书',
                empty: '请开始撰写技术交底书',
            },
            prosecution: {
                withPatentNumber: '开始分析审查意见（申请号：{patentNumber}）',
                withFiles: '开始分析审查意见',
                empty: '请分析审查意见并生成答复文件',
            },
        },

        mention: {
            title: '模板',
            selectType: '选择类型',
            styleTitle: '风格',
            priorArt: '现有技术',
            file: '文件',
            document: '文档',
            figure: '附图',
            loading: '加载中...',
            navigate: '导航',
            select: '选择',
            close: '关闭',
            noResults: '未找到 "{query}" 相关结果',
            tryDifferent: '尝试其他搜索词',
            applicationDate: '申请:',
            publicationDate: '公开:',
            docTypes: {
                patent_draft: '专利撰写',
                disclosure: '技术交底书',
                prosecution: '答复审查意见',
            },
            docStatus: {
                draft: '草稿',
                in_review: '审核中',
                approved: '已批准',
                filed: '已提交',
            },
            types: {
                template: {
                    label: '模板',
                    desc: '专利申请模板',
                },
                style: {
                    label: '撰写风格',
                    desc: '撰写风格设置',
                },
                priorArt: {
                    label: '现有技术',
                    desc: '现有技术专利',
                },
                file: {
                    label: '文件',
                    desc: '附件文件',
                },
                document: {
                    label: '文档',
                    desc: '项目文档',
                },
                figure: {
                    label: '附图',
                    desc: '项目附图',
                },
            },
            templates: {
                usptoUtilityPatent: 'USPTO 实用专利',
                usptoSoftwarePatent: 'USPTO 软件专利',
                epoPatentApplication: 'EPO 专利申请',
                pctInternationalApplication: 'PCT 国际申请',
                cnipaPatentApplication: 'CNIPA 专利申请',
            },
        },

        tooltips: {
            uploadFile: '上传文件',
            voiceInput: '语音输入',
        },

        patent: {
            jurisdiction: '请选择申请地区',
        },

        oar: {
            title: '审查意见答复',
            projectTitle: '审查意见答复 - {patentNumber}',
            projectTitleWithoutNumber: '审查意见答复',
            initialPrompt: '开始分析审查意见',
            initialPromptWithNumber: '开始分析审查意见（申请号：{patentNumber}）',
            confirmMessage: '已确认OAR分析结果，请继续处理',
            oarExtractList: 'OAR 提取列表',
            rejectionTableTitle: '驳回分析表',
            opinionStatementTitle: '意见陈述书',
            amendedClaimsTitle: '权利要求替换页',
            amendmentComparisonTitle: '对照页',
            rejectionAnalysis: {
                title: '驳回分析',
                status: {
                    extracting: '提取数据中',
                    processing: '处理中',
                },
                button: {
                    confirm: '确认并继续',
                    saving: '保存中',
                    addClause: '添加条款',
                    showMore: '展开全部',
                    showLess: '收起',
                    export: '导出 Excel',
                    exportSheetName: '驳回分析',
                    viewDetail: '查看逻辑链',
                    delete: '删除',
                },
                detail: {
                    drawerTitle: '分析详情',
                    examinerOpinion: '审查员意见',
                    featureComparison: '特征对比',
                    claimFeature: '本申请特征',
                    priorArtFeature: '对比文件特征',
                    priorArtLabel: '对比文件{no} · {name}',
                    noPriorArt: '无对比文件',
                    strategy: '答复策略',
                    prevItem: '上一条',
                    nextItem: '下一条',
                },
                direction: {
                    agree: '认可',
                    unsure: '不确定',
                    disagree: '不认可',
                },
                basicInfo: {
                    title: '基础信息',
                    application: '申请号',
                    oaNo: '审查意见编号',
                    type: '类型',
                    examiner: '审查员',
                    issueDate: '发文日',
                    priorArt: '对比文件',
                    applicationFiles: '申请文件',
                },
                workbench: {
                    caseTitleFallback: '专利申请答复策略',
                    examinerConclusionTitle: '审查意见摘要',
                    examinerConclusionPrefix: '审查员认为：{conclusion}',
                    patentTypeInvention: '发明专利',
                    patentTypeUtilityModel: '实用新型专利',
                    viewMaterials: '材料与审查历程',
                    officeActionHistory: '审查历程',
                    round: '第 {round} 轮审查',
                    current: '当前',
                    claimsFile: '被审权利要求',
                    overallStrategy: '整体答复策略',
                    overallFallback: '当前未提供案件级整合结论，请逐项审核下方答复策略。',
                    sectionTabsLabel: '驳回分析视图',
                    caseOverviewTab: '案件概览',
                    documentComparisonTab: '区别特征分析',
                    issueStrategiesTab: '分项答复策略',
                    issueView: '按驳回事项',
                    documentComparison: '区别技术特征汇总',
                    applicationColumnTitle: '本申请',
                    applicationFeatureColumnSubtitle: '权利要求技术特征',
                    priorArtColumnTitle: '对比文件{index}',
                    naturalFeatureTitle: '技术特征 {index}',
                    comparisonConflict: '结论冲突',
                    comparisonNeedsConfirmation: '判断不一致',
                    comparisonFullyCorresponding: '完全对应',
                    comparisonPartiallyCorresponding: '部分公开',
                    comparisonClearlyDifferent: '明确不同',
                    comparisonNotFound: '未发现对应公开',
                    comparisonNeedsVerification: '证据不足，待核验',
                    comparisonNoConclusion: '暂无结论',
                    judgmentCount: '共 {count} 条判断',
                    pendingJudgmentCount: '其中 {count} 条待核验',
                    viewAllJudgments: '查看全部 {count} 条判断',
                    horizontalScrollLabel: '横向查看全部对比文件',
                    viewComparisonDetails: '查看详情',
                    comparisonDetails: '特征对比详情',
                    additionalDifferences: '另有 {count} 条判断',
                    notCompared: '未参与该项比较',
                    noDocumentComparison: '当前没有可汇总的区别技术特征。',
                    issueCount: '审查问题',
                    amendmentCount: '建议修改',
                    riskCount: '风险提示',
                    compactAmendments: '整体修改方案',
                    compactResponse: '简要答复思路',
                    compactNoAmendments: '不修改权利要求',
                    compactStale: '策略已更新，当前概览已过期，请查看分项答复策略。',
                    compactMissing: '简要答复思路尚未生成，可查看完整说明或分项答复策略。',
                    compactMainNoAmendments: '主请求不修改权利要求',
                    compactAmendmentsMissing: '修改方案摘要尚未生成，请查看分项答复策略。',
                    compactPending: '分项策略尚未完成，请稍后查看。',
                    compactFullDetails: '查看完整说明',
                    compactHideDetails: '收起完整说明',
                    keyAmendments: '权利要求修改',
                    multipleAmendmentsWarning: '同一权利要求存在多套逐问题修改方案，请在生成答复前统一合并。',
                    viewIssueStrategy: '查看对应分项答复策略',
                    issueNavigation: '审查问题导航',
                    issueStrategy: '权利要求策略',
                    issue: '审查问题',
                    responseMethod: '答复方式',
                    strategySummary: '策略摘要',
                    risksOrPending: '风险或待确认事项',
                    noRisksOrPending: '暂无风险或待确认事项',
                    detailAnalysis: '详细分析',
                    amendmentDetails: '详细修改方案与完整权利要求',
                    inventiveStepDetails: '创造性三步法分析',
                    problemSolutionApproach: '问题-解决方法分析',
                    comparisonEvidenceDetails: '关键特征、对比文件与附图证据',
                    claimAmendment: '权利要求修改',
                    examinerStartingPoint: '审查员推理起点',
                    amendmentPlan: '权利要求修改方案',
                    amendmentPlanHint: '审核修改方式、原始依据和保护范围影响。',
                    amendmentMethod: '修改方式',
                    scopeImpact: '保护范围影响',
                    amendmentBasis: '修改依据',
                    amendmentReason: '采用该修改的理由',
                    viewAmendedClaims: '查看完整修改后权利要求',
                    threeStepMethod: '创造性三步法',
                    closestPriorArt: '确定最接近的现有技术',
                    differencesAndProblem: '确定区别特征及实际解决的技术问题',
                    distinguishingFeatures: '区别特征',
                    technicalEffects: '说明书记载的技术效果',
                    objectiveTechnicalProblem: '实际解决的技术问题',
                    objectiveProblemGap: '尚未明确，请结合区别特征及其技术效果进一步确认。',
                    technicalMotivation: '判断现有技术是否给出技术启示',
                    coreFeature: '核心技术特征',
                    comparisonAnalysis: '综合对比分析',
                    priorArtAnalysis: '对比文件分析',
                    argumentPath: '答复方案',
                    responseConclusion: '答复结论',
                    featureComparison: '关键特征对比',
                    featureComparisonHint: '权利要求特征与对比文件结论逐项对齐。',
                    drawingEvidence: '关联附图证据',
                    drawingEvidenceHint: '展示与当前特征和答复策略相关的本申请及对比文件图片。',
                    applicationFeature: '本申请权利要求特征',
                    priorArtConclusion: '对比文件比较结论',
                    essentialFeature: '必要特征',
                    nonEssentialFeature: '非必要特征',
                    viewPriorArtExcerpt: '查看对比文件摘录',
                    priorArtLocation: '对比文件证据位置',
                    embodimentBasis: '查看同一实施例依据',
                    noComparison: '当前没有可展示的特征比较。',
                    risks: '相关风险',
                    risksHint: '需要答复人重点审核的案件特有风险。',
                    priorArtDocument: '对比文件',
                    viewClaimsSource: '查看当前被审权利要求原文',
                    historyConsistency: '历史一致性',
                    noPriorArt: '当前数据未提供对比文件摘要。',
                    notProvided: '未提供',
                    edit: '编辑',
                    editOverallStrategy: '编辑整体答复策略',
                    editFeatureSummary: '编辑特征',
                    editComparison: '编辑结论',
                    editingInPlace: '正在原位编辑',
                    markdownSupported: '支持 Markdown',
                    save: '保存',
                    cancel: '取消',
                    editResponseStrategy: '编辑答复策略',
                    amendedClaimText: '完整修改后权利要求',
                    amendmentSourceType: '修改来源类型',
                    sourceClaims: '来源权利要求（用逗号分隔）',
                    sourceLocations: '原始依据位置（用分号分隔）',
                    whyDependentInsufficient: '仅合并从属权利要求仍不足的原因',
                    whyAmendmentOneInsufficient: '适度限缩仍不足的原因',
                    oneRiskPerLine: '每行一项，最多四项',
                    completeRequiredFields: '请补全必填字段后保存',
                    markEssential: '标记为必要特征',
                    evidenceReadonlyHint: '证据原文和位置保持只读',
                    generateResponseDocument: '生成答复文档',
                    regenerateStrategy: '重新生成答复策略',
                    regenerateScope: '重新生成范围',
                    currentIssue: '当前审查问题',
                    allIssues: '全部审查问题',
                    regenerateRequirement: '补充要求',
                    regenerateRequirementPlaceholder: '例如：重点重新核查 ISSUE_001 的区别特征和修改必要性',
                    regenerateOverwriteHint: '重新生成会覆盖所选审查问题的当前策略；案件级人工表述将保留。',
                    confirmRegenerate: '确认重新生成',
                    saveEditorFirst: '请先保存或取消当前正在编辑的内容',
                    openTargetDocumentFirst: '请先打开或新建一个目标答复文档',
                    generateChatMessage: '请基于当前数据生成答复文档',
                    regenerateChatMessage: '用户要求重新生成驳回答复策略。驳回分析数据 ID 为 {rejectionDataId}；重新生成范围为 {scope}；补充要求为 {requirement}。请按照上述范围重新分析并展示最新驳回分析结果。',
                    noAdditionalRequirement: '无',
                    sourceType: {
                        dependentClaim: '从属权利要求',
                        claim: '其他权利要求',
                        specification: '说明书',
                        drawing: '附图',
                        multipleSources: '多个来源',
                    },
                    decision: {
                        argueNoAmend: '仅陈述意见，不修改',
                        mergeDependent: '合并从属权利要求并陈述意见',
                        amendOne: '适度限缩权利要求并陈述意见',
                        amendTwo: '进一步限缩并陈述意见',
                        legacy: '沿用原分析策略',
                    },
                    comparisonStatus: {
                        fullyCorresponding: '完全对应',
                        partiallyCorresponding: '部分公开',
                        clearlyDifferent: '明确不同',
                        notFound: '未发现对应公开',
                        evidenceMissing: '证据缺失',
                        unableToVerify: '无法确认',
                        explicitlyDisclosed: '明示公开',
                        necessarilyImplied: '必然隐含',
                        partiallyDisclosed: '部分公开',
                        notDisclosed: '未公开',
                    },
                    evidencePlaceholder: {
                        notLocatedInSource: '未在原文中定位到',
                        validLocationMissing: '未提供有效定位',
                        priorArtFullTextMissing: '对比文件原文未提供，无法核实披露内容',
                        citedRejectionEvidenceUnavailable: '模型引用的被驳证据不可用',
                        anchorDiagnostic: {
                            startNotFound: '开始锚点未命中：{anchor}',
                            endNotFound: '结束锚点未命中：{anchor}',
                            startMatchedOnce: '开始锚点命中1次（offset={offset}）：{anchor}',
                            endMatchedOnce: '结束锚点命中1次（offset={offset}）：{anchor}',
                            startMatchedMany: '开始锚点命中{count}次（offsets={offsets}）：{anchor}',
                            endMatchedMany: '结束锚点命中{count}次（offsets={offsets}）：{anchor}',
                        },
                    },
                },
                tabs: {
                    all: '全部',
                },
                tableHeaders: {
                    claim: '权利要求',
                    statute: '法条',
                    claimAndStatute: '权利要求/法条',
                    claimElement: '权利要求要素',
                    examinerReasoning: '审查员理由',
                    aiAnalysis: 'AI分析',
                    strategy: '答复策略',
                },
                statusPills: {
                    agree: '认可',
                    unsure: '不确定',
                    disagree: '不认可',
                },
                labels: {
                    direction: '答复方向',
                    strategy: '答复策略',
                    claimAmendment: '权利要求修改',
                    amendmentMethod: '修改方式',
                    amendmentBasis: '修改依据',
                },
                tooltips: {
                    direction: '是否认同审查员观点',
                    strategy: '针对该驳回理由的答复策略',
                    claimAmendment: '是否需要修改权利要求',
                },
                placeholder: {
                    edit: '点击编辑...',
                    claimElement: '输入权利要求要素...',
                    examinerReasoning: '输入审查员意见...',
                    strategy: '输入答复策略...',
                    amendmentMethod: '输入修改方式...',
                    amendmentBasis: '输入修改依据...',
                },
                emptyState: {
                    noData: '暂无驳回数据',
                    hint: '提取完成后将在此显示驳回分析',
                },
                exportSheetNames: {
                    rejectionAnalysis: '驳回分析',
                    keyFeatureComparison: '关键特征对比',
                },
                exportHeaders: {
                    issue: '审查问题',
                    claim: '权利要求',
                    statute: '法律条款',
                    claimElement: '权利要求要素',
                    examinerReasoning: '审查员意见',
                    direction: '答复方向',
                    strategy: '答复策略',
                    distinguishingFeatures: '区别技术特征',
                    priorArt: '对比文件',
                    amendmentMethod: '修改方案',
                    amendmentText: '修改后权利要求',
                    amendmentBasis: '修改依据',
                    scopeImpact: '保护范围影响',
                    risks: '风险',
                    featureId: '特征编号',
                    applicationLocation: '本申请位置',
                    comparisonStatus: '比较结论',
                    specificDifference: '具体区别',
                    priorArtLocation: '对比文件位置',
                },
                message: {
                    noDataId: '无法获取数据ID',
                    noData: '暂无数据',
                    saveSuccess: '保存成功',
                    saveFailed: '保存失败',
                    deleteSuccess: '删除成功',
                },
            },
            region: {
                uspto: 'USPTO',
                epo: 'EPO',
                cnipa: 'CNIPA',
                jpo: 'JPO',
            },
            patentNumber: {
                label: '专利申请号',
                placeholder: '输入被审查专利的申请号，以获取最新审查意见',
            },
            uploadAction: {
                title: '上传审查意见',
                choose: '选择文件',
                description: '上传需要答复的审查意见PDF/Word文件',
                uploading: '正在上传',
                addMore: '继续上传更多文件',
            },
            uploadedFiles: {
                title: '已上传文件 ({count})',
            },
            fileStatus: {
                preparing: '准备文件...',
                analyzing: '分析内容...',
                finalizing: '即将完成...',
                processing: '处理中...',
                estimatedTime: '预计需要 1-2 分钟',
                completed: '已完成',
                failed: '解析失败',
            },
            actions: {
                agent: 'Agent',
                copilot: 'Copilot',
                start: '开始',
            },
            search: {
                failed: '专利搜索失败，请重试',
                notFound: '未找到专利',
            },
            patent: {
                publicationNumber: '公开(公告)号：',
                publicationDate: '公开公告日：',
                currentApplicant: '[标]当前申请(专利权)人：',
            },
            download: {
                searching: '正在搜索审查意见PDF...',
                downloading: 'PDF下载已开始，请检查浏览器下载',
                success: 'PDF下载成功',
                failed: 'PDF下载失败，请重试',
                notFound: '未找到审查意见PDF，您可以手动上传',
                visitUsptoWebsite: '访问 USPTO 网站下载',
            },
            unsupportedRegion: '暂不支持该地区',
            prosecutionTip: '输入专利申请号/上传审查意见PDF，即可开始',
            rejectionDetail: {
                drawerTitle: '驳回详情',
                examinerOpinion: '审查员意见',
                featureComparison: '特征对比',
                claimFeature: '权利要求特征',
                specificationSource: '说明书原文',
                priorArtFeature: '对比文件特征',
                priorArtLabel: '对比文件{no}（{name}）',
                noPriorArt: '暂无对比文件信息',
                priorArtSource: '{name} 原文',
                distinguishingFeatures: '区别技术特征',
                aiStrategy: 'AI 答复策略',
                crossValidation: '交叉验证',
                prevItem: '上一条',
                nextItem: '下一条',
                direction: {
                    agree: '认可',
                    unsure: '不确定',
                    disagree: '不认可',
                },
                claimAmendment: {
                    title: '权利要求修改建议',
                    method: '修改方式',
                    basis: '修改依据',
                },
            },
        },

        draftingStyle: {
            title: '选择撰写风格',
            stylesHeader: '风格列表',
            previewHeader: '预览',
            cancel: '取消',
            apply: '应用风格',
            loading: '加载中...',
            loadingPreview: '加载预览中...',
            loadError: '加载风格列表失败',
            loadDetailError: '加载风格详情失败',
            noStyles: '暂无可用风格',
            noPreview: '暂无预览内容',
            pleaseSelect: '请先选择风格',
            used: '已使用 {count} 次',
            styles: {
                standard: {
                    name: '标准格式',
                    description: '传统专利格式，带编号段落和正式语言',
                },
                detailed: {
                    name: '详细技术',
                    description: '全面的技术描述，包含丰富的实施例细节',
                },
                concise: {
                    name: '简洁权利要求导向',
                    description: '精简格式，强调清晰有力的权利要求语言',
                },
                international: {
                    name: 'PCT 国际申请',
                    description: '针对 PCT 国际专利申请优化的格式',
                },
                continuation: {
                    name: '延续申请',
                    description: '引用母案申请的延续或分案申请格式',
                },
            },
        },

        templateSelection: {
            title: '选择模板',
            templatesHeader: '模板列表',
            previewHeader: '预览',
            cancel: '取消',
            apply: '应用模板',
            loading: '加载中...',
            loadingPreview: '加载预览中...',
            loadError: '加载模板列表失败',
            loadDetailError: '加载模板详情失败',
            noTemplates: '暂无可用模板',
            noPreview: '暂无预览内容',
            pleaseSelect: '请先选择模板',
        },

        documentModifications: {
            title: '文档修改已应用',
            summary: '共 {count} 条修改',
            streamingSummary: '正在生成修改，已识别 {count} 处',
            streamingTitle: 'AI 正在编辑文档',
            streamingDesc: '正在接收修改内容，后端完成后会自动合并并刷新最新版本。',
            streamPreview: '实时修改流',
            appliedSummary: '已应用 {count} 处修改',
            appliedTitle: '修改已自动应用',
            appliedDesc: '后端已完成文档合并并创建新版本，编辑器会刷新为最新内容。',
            emptyState: '暂无修改明细',
            noContent: '无新增内容',
            oldContent: '旧文本：',
            actions: {
                insert: '插入',
                insertBefore: '在前方插入',
                insertInside: '在内部插入',
                deleteInside: '删除内部内容',
                replace: '替换',
                delete: '删除',
            },
            tableHeaders: {
                index: '#',
                documentId: '文档 ID',
                action: '操作',
                dataId: 'data-id',
                content: '修改内容',
                oldContent: '旧文本',
            },
            exportExcel: '导出 Excel',
        },
    },
};

export default zhCN;
